import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

// Services
import { getTranslateByKey, isEmpty, validIPAddress } from '@/main/services/utils';
import { ipToNumber } from '@/main/services/utilsNumber';

// Types
import { IGridList } from '../integrationRegisterScreen';

// Components
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Box from '@/main/components/shared/box';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import Button from '@/main/components/shared/button/button';
import KogInputIPMaskControlled from '@/main/components/shared/inputText/inputIPMaskControlled';

// Components/Styles Local

import * as S from '../styles'
import { toast } from 'react-toastify';

interface IModalIntegration {
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  gridData: IGridList[];
  setGridData: React.Dispatch<React.SetStateAction<IGridList[]>>;
  selectGuidForEdit: string | null;
}

function ModalIntegration({
  isOpenModal,
  setIsOpenModal,
  gridData,
  setGridData,
  selectGuidForEdit = null
}: IModalIntegration) {
  const [errorIpStart, setErrorIpStart] = useState<string>('');
  const [errorIpEnd, setErrorIpEnd] = useState<string>('');

  const INITIAL_FORM_STATE = {
    guidIntegr_IntegrInterface_Company_IPAddress: null as string | null,
    guid: null as string | null,
    ipAddressStart: null as string | null,
    ipAddressEnd: null as string | null,
  };
  type IFormModalIntegration = typeof INITIAL_FORM_STATE;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<IFormModalIntegration>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  const ipStartWatcher = watch('ipAddressStart');
  const ipEndWatcher = watch('ipAddressEnd');

  function checkDuplicate(prev: IGridList[], data: IGridList, guidEdit: string | null) {
    return prev.some(item =>
      ipToNumber(item.ipAddressStart) === ipToNumber(data.ipAddressStart) &&
      ipToNumber(item.ipAddressEnd) === ipToNumber(data.ipAddressEnd) &&
      item.guidList !== guidEdit
    );
  };

  function checkWithinRange(prev: IGridList[], data: IGridList) {
    return prev.some(item =>
      (ipToNumber(data.ipAddressStart) >= ipToNumber(item.ipAddressStart) && ipToNumber(data.ipAddressStart) <= ipToNumber(item.ipAddressEnd)) ||
      (ipToNumber(data.ipAddressEnd) >= ipToNumber(item.ipAddressStart) && ipToNumber(data.ipAddressEnd) <= ipToNumber(item.ipAddressEnd)) ||
      (ipToNumber(data.ipAddressStart) <= ipToNumber(item.ipAddressStart) && ipToNumber(data.ipAddressEnd) >= ipToNumber(item.ipAddressEnd))
    );
  };

  function updateGridData(prev: IGridList[], data: IGridList, guidEdit: string | null) {
    return prev.map(item => {
      if (item.guidList === guidEdit) {
        return data;
      } else {
        return item;
      }
    });
  };


  function onSubmit(data: IFormModalIntegration) {
    if (!isEmpty(errorIpEnd) || !isEmpty(errorIpStart)) return;
    const request: IGridList = {
      guidList: selectGuidForEdit || uuidv4(),
      guidIntegr_IntegrInterface_Company_IPAddress: selectGuidForEdit || '',
      ipAddressStart: data.ipAddressStart || '',
      ipAddressEnd: data.ipAddressEnd || '',
    };
    const prev = gridData;
    const isDuplicate = checkDuplicate(prev, request, selectGuidForEdit);
    const isWithinRange = checkWithinRange(prev, request);

    if (!selectGuidForEdit && (isDuplicate || isWithinRange)) {
      setErrorIpStart(getTranslateByKey('information.duplicatedIP', true));
      setErrorIpEnd(getTranslateByKey('information.duplicatedIP', true));
      return prev;
    }
    if (selectGuidForEdit) {
      const updatedGridData = updateGridData(prev, request, selectGuidForEdit);
      reset(INITIAL_FORM_STATE);
      setIsOpenModal(false);
      toast.success(getTranslateByKey('information.ipRestricitonAddedSuccessfully'));
      return setGridData(updatedGridData);
    } else {
      if (isDuplicate || isWithinRange) {
        setErrorIpStart(getTranslateByKey('information.duplicatedIP', true));
        setErrorIpEnd(getTranslateByKey('information.duplicatedIP', true));
        return prev;
      }
      reset(INITIAL_FORM_STATE);
      setIsOpenModal(false);
      toast.success(getTranslateByKey('information.ipRestricitonAddedSuccessfully'));
      return setGridData([...prev, request]);
    }
  }

  function handleOnBlurIPStart() {
    if (ipStartWatcher && validIPAddress(ipStartWatcher)) {
      setValue('ipAddressEnd', ipStartWatcher)
    }
  }

  useEffect(() => {
    if (!isOpenModal) {
      reset(INITIAL_FORM_STATE);
    }
  }, [isOpenModal])

  useEffect(() => {
    if (selectGuidForEdit && gridData) {
      const findData = gridData.find(x => x.guidList === selectGuidForEdit);
      if (findData) {
        setValue('ipAddressStart', findData.ipAddressStart);
        setValue('ipAddressEnd', findData.ipAddressEnd);
      }
    }
  }, [selectGuidForEdit])

  useEffect(() => {
    if (ipStartWatcher)
      if (validIPAddress(ipStartWatcher)) {
        setErrorIpStart('');
      } else {
        setErrorIpStart(getTranslateByKey('information.invalidIP', true).replace(" {0}", ""))
      }
  }, [ipStartWatcher]);

  useEffect(() => {
    if (ipEndWatcher)
      if (validIPAddress(ipEndWatcher)) {
        setErrorIpEnd('');
      } else {
        setErrorIpEnd(getTranslateByKey('information.invalidIP', true).replace(" {0}", ""))
      }
  }, [ipEndWatcher]);

  return (
    <KogModalSideComponent
      open={isOpenModal}
      closeModal={
        () => {
          reset(INITIAL_FORM_STATE);
          setIsOpenModal(false);
        }
      }
      title={getTranslateByKey('information.ipRestriction')}
      titleModalReducer='ipRestriction'
    >
      {isOpenModal && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box>
            <S.RowInputModal>
              <KogInputIPMaskControlled
                name="ipAddressStart"
                typeInput='filled'
                label={getTranslateByKey('information.ipStart')}
                control={control}
                controllerProps={{ rules: { required: true } }}
                errorMessageCustom={
                  errors.ipAddressStart?.type === 'required'
                    ? getTranslateByKey('information.ipStartRequired', true)
                    : '' ||
                      errorIpStart
                      ? errorIpStart
                      : ''
                }
                valueCustom={ipStartWatcher}
                error={!!errors.ipAddressStart || !!errorIpStart}

                id="id-registerIntegrationModal-txt-ipAddressStart"
                required
                onBlur={handleOnBlurIPStart}
                setValueCustom={setValue}
              />
              <KogInputIPMaskControlled
                name="ipAddressEnd"
                typeInput='filled'
                label={getTranslateByKey('information.ipEnd')}
                control={control}
                controllerProps={{ rules: { required: true } }}
                errorMessageCustom={
                  errors.ipAddressEnd?.type === 'required'
                    ? getTranslateByKey('information.ipEndRequired', true)
                    : '' ||
                      errorIpEnd
                      ? errorIpEnd
                      : ''
                }
                valueCustom={ipEndWatcher}
                error={!!errors.ipAddressEnd || !!errorIpEnd}
                id="id-registerIntegrationModal-txt-ipAddressEnd"
                required
                setValueCustom={setValue}
              />
            </S.RowInputModal>
          </Box>

          <S.AreaButton>
            <SecondaryButton
              type='button'
              id="id-registerIntegrationModal-btn-save"
              onClick={() => {
                reset(INITIAL_FORM_STATE);
                setIsOpenModal(false);
              }}
            >
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            <Button
              type='submit'
              id="id-registerIntegrationModal-btn-save"
            >
              {getTranslateByKey('information.save')}
            </Button>
          </S.AreaButton>
        </form>
      )}
    </KogModalSideComponent >
  )
}

export default ModalIntegration;